import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTemplates } from '@/shared/hooks/useCloudData';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ChevronDown, Send, Copy, Check, Loader2, Link } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: string | null;
  clientEmail: string;
  clientName: string;
}

export function SendConsentDialog({ open, onClose, clientId, clientEmail, clientName }: Props) {
  const { language } = useLanguage();
  const { data: templates = [] } = useTemplates();
  const activeTemplates = templates.filter(t => !t.archived);

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [email, setEmail] = useState(clientEmail);
  const [sending, setSending] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const selectedTemplate = activeTemplates.find(t => t.id === selectedTemplateId);

  const handleSend = async () => {
    if (!selectedTemplateId) { toast.error('Choisissez un template'); return; }
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('send-consent-link', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: {
          template_id: selectedTemplateId,
          client_id: clientId,
          client_email: email.trim() || null,
          client_name: clientName || null,
          template_name: selectedTemplate?.name[language] ?? selectedTemplate?.name['fr'],
        },
      });
      if (error) throw error;
      setGeneratedLink(data.link_url);
      if (email.trim()) toast.success('Lien envoyé par email');
      else toast.success('Lien généré');
    } catch (err: any) {
      toast.error(err?.message || 'Erreur lors de la génération du lien');
    } finally {
      setSending(false);
    }
  };

  const handleCopy = () => {
    if (!generatedLink) return;
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Lien copié');
  };

  const handleClose = () => {
    setSelectedTemplateId('');
    setEmail(clientEmail);
    setGeneratedLink(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && handleClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link size={18} className="text-primary" />
            Envoyer un lien de consentement
          </DialogTitle>
        </DialogHeader>

        {!generatedLink ? (
          <div className="space-y-4 mt-2">
            {/* Template picker */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Formulaire</label>
              <div className="relative">
                <select
                  value={selectedTemplateId}
                  onChange={e => setSelectedTemplateId(e.target.value)}
                  className="w-full p-3 bg-card border border-border rounded-xl text-sm focus:border-primary focus:outline-none appearance-none pr-8"
                >
                  <option value="">Choisir un formulaire…</option>
                  {activeTemplates.map(t => (
                    <option key={t.id} value={t.id}>{t.name[language] || t.name['fr']}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            {/* Email input */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Email du client</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="client@email.com"
                className="w-full p-3 bg-card border border-border rounded-xl text-sm focus:border-primary focus:outline-none"
              />
              <p className="text-xs text-muted-foreground">Laissez vide pour obtenir uniquement le lien sans email.</p>
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={handleClose}
                className="flex-1 py-2.5 border border-border rounded-xl text-sm text-muted-foreground hover:bg-secondary transition-colors">
                Annuler
              </button>
              <button onClick={handleSend} disabled={sending || !selectedTemplateId}
                className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {email.trim() ? 'Envoyer' : 'Générer le lien'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-xl">
              <Check size={16} className="text-primary shrink-0" />
              <p className="text-sm text-primary font-medium">
                {email.trim() ? 'Email envoyé avec succès' : 'Lien généré'}
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground font-medium">Lien de consentement (valide 7 jours)</label>
              <div className="flex items-center gap-2 p-3 bg-secondary/60 border border-border/50 rounded-xl">
                <p className="text-xs text-foreground flex-1 truncate font-mono">{generatedLink}</p>
                <button onClick={handleCopy}
                  className="shrink-0 p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
                  {copied ? <Check size={14} className="text-primary" /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            <button onClick={handleClose}
              className="w-full py-2.5 bg-secondary rounded-xl text-sm font-medium hover:bg-secondary/80 transition-colors">
              Fermer
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
